const DATE_FORMATTER = new Intl.DateTimeFormat('hr-HR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const TIME_FORMATTER = new Intl.DateTimeFormat('hr-HR', {
  hour: '2-digit',
  minute: '2-digit',
});

/** Croatian dates carry a trailing dot: 21.10.2026. */
export function formatDate(iso: string): string {
  const parsed = new Date(iso);

  if (Number.isNaN(parsed.getTime())) {
    return iso;
  }

  return `${DATE_FORMATTER.format(parsed).replace(/\s/g, '')}`;
}

export function formatTime(iso: string): string {
  const parsed = new Date(iso);

  return Number.isNaN(parsed.getTime()) ? '' : TIME_FORMATTER.format(parsed);
}

export function formatUptime(seconds: number): string {
  if (seconds < 60) {
    return `${String(seconds)}s`;
  }

  const minutes = Math.floor(seconds / 60);

  if (minutes < 60) {
    return `${String(minutes)}m ${String(seconds % 60)}s`;
  }

  return `${String(Math.floor(minutes / 60))}h ${String(minutes % 60)}m`;
}

interface RangeStyle {
  /** Month names in the form used after a day number. */
  readonly months: readonly string[];
  readonly day: (day: number) => string;
  readonly year: (year: number) => string;
}

const dotted = (value: number): string => `${String(value)}.`;

/**
 * Written out per language rather than taken from `Intl`: browsers ship
 * different locale data, and one without Macedonian silently formats in the
 * browser's own language — a Macedonian fair showed "19 – 26 October 2026".
 */
const RANGE_STYLES: Record<string, RangeStyle> = {
  hr: {
    months: [
      'siječnja',
      'veljače',
      'ožujka',
      'travnja',
      'svibnja',
      'lipnja',
      'srpnja',
      'kolovoza',
      'rujna',
      'listopada',
      'studenoga',
      'prosinca',
    ],
    day: dotted,
    year: dotted,
  },
  sr: {
    months: [
      'januar',
      'februar',
      'mart',
      'april',
      'maj',
      'jun',
      'jul',
      'avgust',
      'septembar',
      'oktobar',
      'novembar',
      'decembar',
    ],
    day: dotted,
    year: dotted,
  },
  bs: {
    months: [
      'januar',
      'februar',
      'mart',
      'april',
      'maj',
      'juni',
      'juli',
      'august',
      'septembar',
      'oktobar',
      'novembar',
      'decembar',
    ],
    day: dotted,
    year: dotted,
  },
  mk: {
    months: [
      'јануари',
      'февруари',
      'март',
      'април',
      'мај',
      'јуни',
      'јули',
      'август',
      'септември',
      'октомври',
      'ноември',
      'декември',
    ],
    day: String,
    // A narrow no-break space keeps "г." from wrapping onto a line of its own.
    year: (year) => `${String(year)}\u202Fг.`,
  },
};

/**
 * "20. – 27. listopada 2026." — the month and year are stated once when the fair
 * does not cross a month boundary, which is how the design reads.
 *
 * Formatting is pinned to UTC because the API sends the fair's end as
 * 23:59:59Z; rendering that in a local zone ahead of UTC would advance the date
 * and advertise the fair as running a day longer than it does.
 */
export function formatDateRange(startIso: string, endIso: string, language = 'hr'): string {
  const start = new Date(startIso);
  const end = new Date(endIso);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return '';
  }

  const style = RANGE_STYLES[language] ?? RANGE_STYLES['hr'];

  if (style === undefined) {
    return '';
  }

  const date = (value: Date): string =>
    `${style.day(value.getUTCDate())} ${style.months[value.getUTCMonth()] ?? ''}`;
  const startYear = start.getUTCFullYear();
  const endYear = end.getUTCFullYear();

  if (startYear !== endYear) {
    return `${date(start)} ${style.year(startYear)} – ${date(end)} ${style.year(endYear)}`;
  }

  if (start.getUTCMonth() === end.getUTCMonth()) {
    return `${style.day(start.getUTCDate())} – ${date(end)} ${style.year(endYear)}`;
  }

  return `${date(start)} – ${date(end)} ${style.year(endYear)}`;
}
